import { cn } from '@/lib/utils';

export type RagStatus = 'green' | 'amber' | 'red';

export interface MarginBadgeProps {
  marginPercent: number;
  targetMargin?: number;
  showTarget?: boolean;
  className?: string;
}

export function getRagStatus(marginPercent: number): RagStatus {
  if (marginPercent >= 60) return 'green';
  if (marginPercent >= 40) return 'amber';
  return 'red';
}

const ragColors: Record<RagStatus, string> = {
  green: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  amber: 'bg-amber-100 text-amber-800 border-amber-200',
  red: 'bg-red-100 text-red-800 border-red-200',
};

const ragDots: Record<RagStatus, string> = {
  green: 'bg-emerald-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
};

export function MarginBadge({
  marginPercent,
  targetMargin = 60,
  showTarget = false,
  className,
}: MarginBadgeProps) {
  const status = getRagStatus(marginPercent);
  const meetsTarget = marginPercent >= targetMargin;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold',
        ragColors[status],
        className
      )}
    >
      <span className={cn('h-2 w-2 rounded-full', ragDots[status])} />
      {marginPercent.toFixed(1)}%
      {showTarget && !meetsTarget && (
        <span className="text-muted-foreground ml-1">(target: {targetMargin}%)</span>
      )}
    </span>
  );
}

export function StatusBadge({
  isActive,
  className,
}: {
  isActive: boolean;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold',
        isActive
          ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
          : 'bg-neutral-100 text-neutral-600 border-neutral-200',
        className
      )}
    >
      <span
        className={cn(
          'h-2 w-2 rounded-full',
          isActive ? 'bg-emerald-500' : 'bg-neutral-400'
        )}
      />
      {isActive ? 'Active' : 'Inactive'}
    </span>
  );
}

const statusStyles: Record<string, string> = {
  ACTIVE: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  DORMANT: 'bg-amber-100 text-amber-800 border-amber-200',
  DELISTED: 'bg-neutral-100 text-neutral-600 border-neutral-200',
  UNMANAGED: 'bg-amber-100 text-amber-950 border-amber-200',
};

export function ProductStatusBadge({
  status,
  className,
}: {
  status: 'ACTIVE' | 'DORMANT' | 'DELISTED' | 'UNMANAGED' | string;
  className?: string;
}) {
  const label =
    status === 'ACTIVE'
      ? 'Active'
      : status === 'DORMANT'
        ? 'Dormant'
        : status === 'UNMANAGED'
          ? 'Unmanaged'
          : 'Delisted';
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold',
        statusStyles[status] ?? statusStyles.DELISTED,
        className
      )}
    >
      {label}
    </span>
  );
}

export default MarginBadge;
